import * as React from "react";
import { css } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Theme,
  useTheme,
} from "@mui/material";
import Link from "next/link";
import { useRouter } from "next/router";
import { MouseEventHandler, useCallback } from "react";
import { match } from "ts-pattern";

import Loading from "~/components/me/bento/Loading";
import RetriableError from "~/components/me/bento/RetriableError";
import ContentBodyLayout from "~/components/me/bento/content/ContentBodyLayout";
import { useMeMembershipShortcutContent } from "~/components/me/bento/lib";
import { useOpenContentModal } from "~/components/me/hooks";
import { dayjs } from "~/lib/date-util";
import { MeMembershipShortcutContentQuery, Sdk } from "~/lib/graphql/client";
import { formatDate } from "~/lib/graphql/graphqlClient";
import { useAppContext } from "~/pages/_app";

const contentName = "MembershipShortcut";

const MembershipShortcut = ({ client }: { client: Sdk }) => {
  const theme = useTheme();
  const router = useRouter();

  const { openedContent, openContentModal, closeContentModal } =
    useOpenContentModal();

  const result = useMeMembershipShortcutContent(client);
  const openModal = () => {
    if (result.status !== "success") return;
    openContentModal(contentName);
  };

  const closeModal = () => closeContentModal();

  const handleCancel = useCallback(
    async (membershipId: number) => {
      const result = confirm("退会しますか？");
      if (result) {
        await client.cancelMembership({ membershipId });
        router.reload();
      }
    },
    [client, router]
  );
  return (
    <>
      {match(result)
        .with({ status: "pending" }, () => <Loading />)
        .with({ status: "error" }, (matched) => (
          <RetriableError reload={matched.refetch} />
        ))
        .with({ status: "success" }, (matched) => (
          <Body content={matched.data} onClick={openModal} />
        ))
        .exhaustive()}
      {result.status === "success" && (
        <Dialog
          open={openedContent === contentName}
          onClose={closeContentModal}
          fullWidth
          PaperProps={dialogStyle(theme)}
        >
          <DialogContent>
            <IconButton onClick={closeModal}>
              <CloseIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>
            <h2>利用中のメンバーシップ</h2>
            <div css={cardListContainerStyle}>
              {result.data.me.enabledMemberships.length === 0 && (
                <p>利用中のメンバーシップはありません</p>
              )}
              {result.data.me.enabledMemberships.map((enabledMembership) => (
                <MembershipCard
                  key={enabledMembership.partnerCategoryMembership.id}
                  enabledMembership={enabledMembership}
                  handleCancel={handleCancel}
                />
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// 障害対応のため enabledMembership.id = 4 の値段を300円に変更
// 文脈: https://www.notion.so/fussy-inc/121e3548b4aa80d394b2dd3258434660?pvs=4
function applyMembershipIncidentFix(
  enabledMembership: MeMembershipShortcutContentQuery["me"]["enabledMemberships"][number]
) {
  if (enabledMembership.partnerCategoryMembership.id === 4) {
    enabledMembership.partnerCategoryMembership.pricePerMonth = 300;
    enabledMembership.partnerCategoryMembership.title = "推し部長養成コース";
  }
  return enabledMembership;
}

const MembershipCard = ({
  enabledMembership,
  handleCancel,
}: {
  enabledMembership: MeMembershipShortcutContentQuery["me"]["enabledMemberships"][number];
  handleCancel: (membershipId: number) => void;
}) => {
  const theme = useTheme();
  const { currentUser } = useAppContext();
  const appliedMembership = applyMembershipIncidentFix(enabledMembership);
  if (!currentUser) return null;

  return (
    <Link
      href={`/category/${enabledMembership.partnerCategoryMembership.category.id}/membership/${enabledMembership.partnerCategoryMembership.id}`}
      css={membershipCardContainerStyle}
    >
      <div css={membershipCardStyle(theme)}>
        <span id={"membership-title"}>
          {enabledMembership.partnerCategoryMembership.title}
        </span>
        <div id={"membership-left-content"}>
          <div>
            <span className={"label"}>Fave</span>
            <span className={"value"}>
              {enabledMembership.partnerCategoryMembership.category.name}
            </span>
          </div>
          <div>
            <span className={"label"}>Name</span>
            <span className={"value"}>{currentUser?.name}</span>
          </div>
        </div>
        <div id={"membership-right-content"}>
          <div>
            <span className={"label"}>Price</span>
            <span className={"value"}>
              {appliedMembership.partnerCategoryMembership.pricePerMonth.toLocaleString()}
              円/月
            </span>
          </div>
          <div>
            <span className={"label"}>Duration</span>
            <span className={"value"}>
              {dayjs().diff(dayjs(enabledMembership.startedAt), "days")}
              day
            </span>
          </div>
        </div>
      </div>
      <div css={metadataStyle(theme)}>
        <p id={"expired-at"}>
          更新予定日：{formatDate(enabledMembership.expiredAt)}
        </p>
        <Button
          id={"cancel-button"}
          css={cancelButtonStyle(theme)}
          onClick={() =>
            handleCancel(enabledMembership.partnerCategoryMembership.id)
          }
          variant={"text"}
        >
          退会する
        </Button>
      </div>
    </Link>
  );
};

const dialogStyle = (theme: Theme) => ({
  sx: {
    width: "100%",
    height: "80vh",
    m: 1,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "100vh",
    },
  },
});

const cardListContainerStyle = css`
  display: flex;
  flex-flow: column;
  gap: 1rem;
  align-items: center;
`;

const membershipCardContainerStyle = css`
  width: 100%;
  max-width: 400px;
`;

const membershipCardStyle = (theme: Theme) => css`
  width: 100%;
  max-width: 400px;
  aspect-ratio: 16 / 9;

  padding: 0.5rem;
  border-radius: 0.5rem;
  background: ${theme.palette.background.default};

  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 40px 1fr;
  gap: 0.5rem;

  #membership-title {
    grid-column: 1 / 3;
    grid-row: 1 / 2;
    font-size: 1.25rem;
    font-weight: 700;
    color: ${theme.palette.primary.main};
    padding: 0.25rem;
    border-radius: 0.375rem 0.375rem 0 0;
  }

  // もう少しうまいコントロールを考えたい
  grid-row-gap: 30%;

  #membership-left-content {
    grid-column: 1 / 2;
    grid-row: 2 / 3;
  }

  #membership-right-content {
    grid-column: 2 / 3;
    grid-row: 2 / 3;
  }

  .label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: ${theme.palette.primary.main};
    margin-top: 0.25rem;
  }

  .value {
    display: block;
    font-size: 1rem;
    font-weight: 600;
    color: ${theme.palette.text.primary};
    margin-left: 0.5rem;
  }
`;

const metadataStyle = (theme: Theme) => css`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem;
  gap: 1rem;

  #expired-at {
    font-size: 0.75rem;
    font-weight: 500;
    color: ${theme.palette.text.primary};
  }

  #cancel-button {
    font-size: 0.75rem;
    font-weight: 700;
    color: ${theme.palette.info.main};
  }
`;

const cancelButtonStyle = (theme: Theme) => css`
  color: ${theme.palette.info.main};
`;

const Body = ({
  content,
  onClick,
}: {
  content: MeMembershipShortcutContentQuery;
  onClick: MouseEventHandler;
}) => {
  const theme = useTheme();
  return (
    <ContentBodyLayout onClick={onClick} label={"Membership"}>
      <div css={membershipShortcutStyle(theme)}>
        {content.me.enabledMemberships.slice(0, 2).map((enabledMembership) => (
          <div
            key={enabledMembership.partnerCategoryMembership.id}
            css={shortcutPanelStyle(theme)}
          >
            <span id={"category-name"}>
              {enabledMembership.partnerCategoryMembership.category.name}
            </span>
            <span id={"membership-title"}>
              {enabledMembership.partnerCategoryMembership.title}
            </span>
          </div>
        ))}
      </div>
    </ContentBodyLayout>
  );
};

const membershipShortcutStyle = (theme: Theme) => css`
  background: ${theme.palette.background.default};
  height: 100%;
  margin: 0.75rem 0.25rem;
  border-radius: 1rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;

  & > div:not(:last-child) {
    border-bottom: 1px solid ${theme.palette.divider};
  }
`;

const shortcutPanelStyle = (theme: Theme) => css`
  padding: 0.25rem 0.5rem;

  #category-name {
    display: block;
    font-size: 0.6rem;
    font-weight: 500;
    color: ${theme.palette.primary.main};
    margin: 0;
  }

  #membership-title {
    display: block;
    font-size: 0.75rem;
    font-weight: 700;
    margin: 0.25rem 0;
  }
`;

export default MembershipShortcut;
